import {
	calculateMaximumObjectSize,
	type MaximumObjectSize,
	type MaximumObjectSizeObjectData,
	type MaximumObjectSizePhotoData,
} from '@sosocio/tools/max-object-size';
import {
	AppDataModule,
	ConfigModule,
	ProductStateModule,
} from 'store';
import {
	shouldApplyUpscaling,
	shouldApplyVectorization,
} from 'tools/low-resolution-warning';

/**
 * Supplies Creator store policy to the shared maximum-size calculation while
 * retaining the established positional utility API.
 *
 * @param objectData Page-object source and crop geometry.
 * @param photoData Optional full source-photo dimensions.
 * @param offeringModel Offering whose DPI and processing policy should apply.
 * @returns Maximum and recommended full/cropped object dimensions.
 */
export default function maxObjectSize(
	objectData: MaximumObjectSizeObjectData,
	photoData?: MaximumObjectSizePhotoData,
	offeringModel = ProductStateModule.getOffering,
): MaximumObjectSize {
	let finalOfferingModel = offeringModel;

	if (
		!finalOfferingModel
		&& window.glPlatform === 'server'
	) {
		finalOfferingModel = (
			ProductStateModule.getOffering
			|| AppDataModule.offerings[0]
		);
	}

	if (!finalOfferingModel) {
		throw new Error('Missing required offering model');
	}

	return calculateMaximumObjectSize({
		objectData,
		offeringDpi: {
			configDpi: finalOfferingModel.configdpi,
			minimumDpi: finalOfferingModel.minimumdpi,
			qualityDpi: finalOfferingModel.qualitydpi,
		},
		photoData,
		policy: {
			applyUpscaling: shouldApplyUpscaling(
				finalOfferingModel,
				ProductStateModule.getProductSettings,
			),
			applyVectorization: shouldApplyVectorization(finalOfferingModel),
			maximumOutputPixels: ConfigModule['upscaling.maxOutput'],
			recommendedMaximumScale: ConfigModule['upscaling.recommendedMaxScale'],
		},
	});
}

export { calculateMaximumObjectSize } from '@sosocio/tools/max-object-size';
export type {
	CalculateMaximumObjectSizeOptions,
	MaximumObjectSize,
	MaximumObjectSizeObjectData,
	MaximumObjectSizeOfferingDpi,
	MaximumObjectSizePhotoData,
	MaximumObjectSizePolicy,
} from '@sosocio/tools/max-object-size';
